<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import Message from '@uiKit/elements/Message.vue'
import SupportTicketForm from '../components/SupportTicketForm.vue'
import { useTicketCategoriesQuery } from '../api/queries/useTicketCategoriesQuery'
import { useCreatePublicSupportTicketMutation } from '../api/mutations/useCreatePublicSupportTicketMutation'
import type { SupportTicketFormValues } from '../types/supportTypes'

// "Create Support Ticket" dialog for a visitor who isn't signed in — opened
// from the Support button on the pre-auth screens. Same form as the signed-in
// dialog, in public mode: the visitor gives a name and email to reply to. Both
// endpoints it calls are public on seller-service, so nothing here can bounce
// the visitor to /login.
//
// No confirmation email is sent yet, so the reference code is the visitor's
// only receipt. It stays on screen inside the dialog rather than in a toast
// that would vanish before they could note it down.
const open = defineModel<boolean>({ required: true })

const categoriesQuery = useTicketCategoriesQuery()
const createMutation = useCreatePublicSupportTicketMutation()

const categories = computed(() => categoriesQuery.data.value ?? [])
const apiError = computed(() => createMutation.error.value?.message ?? '')
const referenceCode = computed(() => createMutation.data.value ?? '')

function handleSubmit(values: SupportTicketFormValues) {
  createMutation.mutate({
    name: values.name,
    email: values.email,
    categoryId: values.categoryId,
    subject: values.subject,
    description: values.description,
  })
}

// The mutation outlives the dialog (it belongs to this container, which stays
// mounted), so closing resets it — the next opening starts on a blank form,
// not on the last request's receipt.
function handleClose() {
  if (createMutation.isPending.value) return
  open.value = false
  createMutation.reset()
}
</script>

<template>
  <!-- aria-label: PrimeVue's aria-labelledby points at its own header, which a
       custom #header replaces, so the dialog is named here instead. -->
  <Dialog
    :model-value="open"
    width="660px"
    is-flush
    :show-close-icon="false"
    aria-label="Create Support Ticket"
    @update:model-value="!$event && handleClose()"
  >
    <template #header>
      <Box flex align="center" justify="between" gap="4" class="public-ticket__head">
        <Text is="h2" type="title-lg" weight="semibold">Create Support Ticket</Text>
        <Button
          icon="times"
          variant="text"
          color="secondary"
          size="small"
          aria-label="Close"
          class="public-ticket__close"
          :disabled="createMutation.isPending.value"
          @click="handleClose"
        />
      </Box>
    </template>

    <Box class="public-ticket__scroll">
      <Box v-if="referenceCode" direction="col" gap="4" align="start" padding="1.75rem">
        <Text is="h3" type="page-title">Request received</Text>
        <Text type="body">
          Your reference is <Text is="strong" type="body" weight="bold">{{ referenceCode }}</Text
          >. Keep it handy — quote it if you contact us again about this request. Our support team
          will reply by email.
        </Text>
        <Button label="Done" size="large" @click="handleClose" />
      </Box>

      <Box v-else-if="categoriesQuery.isError.value" padding="1.75rem">
        <Message color="danger" variant="outlined">
          <Box flex align="center" justify="between" gap="3" class="public-ticket__error">
            <Text is="span" type="body-sm" color="inherit">We couldn't load the categories.</Text>
            <Button
              label="Try again"
              variant="text"
              size="small"
              :loading="categoriesQuery.isFetching.value"
              @click="categoriesQuery.refetch()"
            />
          </Box>
        </Message>
      </Box>

      <SupportTicketForm
        v-else
        mode="public"
        :categories="categories"
        :is-categories-loading="categoriesQuery.isPending.value"
        :loading="createMutation.isPending.value"
        :api-error="apiError"
        @submit="handleSubmit"
        @cancel="handleClose"
      />
    </Box>
  </Dialog>
</template>

<style scoped>
.public-ticket__head {
  padding: 1.375rem 1.5rem 1.25rem 1.75rem;
  border-bottom: 1px solid var(--p-content-border-color);
}

/* The grey 32px dot around the X. The class lands on the .p-button root. */
.public-ticket__close {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 50%;
  background: var(--p-surface-100);
}

/* The flush panel caps its height; the form scrolls inside it on short
   viewports instead of pushing the footer off-screen. */
.public-ticket__scroll {
  min-height: 0;
  overflow-y: auto;
}

.public-ticket__error {
  flex-wrap: wrap;
  width: 100%;
}
</style>
